import type {
  BillingContact,
  BillingHistory,
  BillingOverview,
  CurrentPlan,
  PaymentMethod,
  PlanUpgradeOptions,
  UpdateBillingContactRequest,
  UsageStats,
} from '../types/billing.types'

class BillingService {
  private billingData: BillingOverview

  constructor() {
    this.billingData = this.generateMockBillingData()
  }

  private delay(ms: number = 300): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private generateMockBillingData(): BillingOverview {
    const currentPeriodStart = new Date('2024-08-01')
    const currentPeriodEnd = new Date('2024-09-01')

    return {
      current_plan: {
        id: 'plan-pro',
        name: 'Professional',
        description: 'Perfect for growing teams with advanced monitoring needs',
        price: 99,
        currency: 'USD',
        billing_period: 'monthly',
        features: [
          'Unlimited events',
          'Up to 10 team members',
          'Advanced analytics',
          'Priority support',
          'Custom webhooks',
          'API access',
        ],
        limits: {
          events_per_month: null, // unlimited
          seats: 10,
          webhooks: 50,
          api_calls_per_day: 10000,
        },
        status: 'active',
        current_period_start: currentPeriodStart.toISOString(),
        current_period_end: currentPeriodEnd.toISOString(),
        auto_renew: true,
      },
      usage_stats: {
        current_period: {
          events_used: 47329,
          events_limit: null,
          events_overage: 0,
          seats_used: 8,
          seats_limit: 10,
          webhooks_used: 3,
          webhooks_limit: 50,
          api_calls_used: 156789,
          api_calls_limit: 10000,
        },
        previous_period: {
          events_used: 41250,
          seats_used: 7,
          webhooks_used: 2,
          api_calls_used: 142300,
        },
        usage_trends: {
          events_trend: 14.7, // 14.7% increase
          seats_trend: 14.3,
          api_calls_trend: 10.2,
        },
      },
      billing_contact: {
        name: 'John Smith',
        email: 'billing@acmecorp.com',
        company: 'Acme Corporation',
        address: {
          street: '123 Business Ave, Suite 100',
          city: 'New York',
          state: 'NY',
          postal_code: '10001',
          country: 'United States',
        },
        tax_id: 'TAX123456789',
      },
      payment_methods: [
        {
          id: 'pm-001',
          type: 'card',
          is_default: true,
          card: {
            brand: 'Visa',
            last4: '4242',
            exp_month: 12,
            exp_year: 2026,
          },
          created_at: '2024-01-15T10:00:00Z',
        },
        {
          id: 'pm-002',
          type: 'card',
          is_default: false,
          card: {
            brand: 'Mastercard',
            last4: '8888',
            exp_month: 8,
            exp_year: 2025,
          },
          created_at: '2024-06-20T14:30:00Z',
        },
      ],
      recent_invoices: this.generateMockInvoices(),
      next_billing_date: currentPeriodEnd.toISOString(),
      current_balance: 0,
      overdue_amount: 0,
    }
  }

  private generateMockInvoices(): BillingHistory[] {
    const invoices: BillingHistory[] = []

    for (let i = 0; i < 6; i++) {
      const invoiceDate = new Date()
      invoiceDate.setMonth(invoiceDate.getMonth() - i)

      const periodStart = new Date(invoiceDate)
      periodStart.setDate(1)

      const periodEnd = new Date(periodStart)
      periodEnd.setMonth(periodEnd.getMonth() + 1)
      periodEnd.setDate(0)

      const dueDate = new Date(invoiceDate)
      dueDate.setDate(dueDate.getDate() + 30)

      const paidDate = new Date(invoiceDate)
      paidDate.setDate(paidDate.getDate() + Math.floor(Math.random() * 15))

      const status =
        i === 0 ? (Math.random() > 0.8 ? 'pending' : 'paid') : 'paid'

      invoices.push({
        id: `inv-${String(Date.now() - i * 86400000).slice(-8)}`,
        invoice_number: `INV-${new Date().getFullYear()}-${String(1000 + i).padStart(4, '0')}`,
        amount: 99 + (Math.random() > 0.8 ? Math.floor(Math.random() * 50) : 0), // occasional overage
        currency: 'USD',
        status: status as BillingHistory['status'],
        description: `Professional Plan - ${periodStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
        period_start: periodStart.toISOString(),
        period_end: periodEnd.toISOString(),
        issued_date: invoiceDate.toISOString(),
        due_date: dueDate.toISOString(),
        paid_date: status === 'paid' ? paidDate.toISOString() : undefined,
        download_url:
          status === 'paid'
            ? `/api/invoices/inv-${String(Date.now() - i * 86400000).slice(-8)}/download`
            : undefined,
      })
    }

    return invoices
  }

  async getBillingOverview(): Promise<BillingOverview> {
    await this.delay()
    return { ...this.billingData }
  }

  async getCurrentPlan(): Promise<CurrentPlan> {
    await this.delay()
    return { ...this.billingData.current_plan }
  }

  async getUsageStats(): Promise<UsageStats> {
    await this.delay()
    return { ...this.billingData.usage_stats }
  }

  async getBillingContact(): Promise<BillingContact> {
    await this.delay()
    return { ...this.billingData.billing_contact }
  }

  async updateBillingContact(
    data: UpdateBillingContactRequest
  ): Promise<BillingContact> {
    await this.delay(800)

    this.billingData.billing_contact = {
      ...this.billingData.billing_contact,
      ...data,
      address: data.address
        ? { ...this.billingData.billing_contact.address, ...data.address }
        : this.billingData.billing_contact.address,
    }

    return { ...this.billingData.billing_contact }
  }

  async getPaymentMethods(): Promise<PaymentMethod[]> {
    await this.delay()
    return [...this.billingData.payment_methods]
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    await this.delay()

    this.billingData.payment_methods = this.billingData.payment_methods.map(
      pm => ({
        ...pm,
        is_default: pm.id === paymentMethodId,
      })
    )
  }

  async getBillingHistory(limit: number = 12): Promise<BillingHistory[]> {
    await this.delay()
    return this.billingData.recent_invoices.slice(0, limit)
  }

  async downloadInvoice(invoiceId: string): Promise<{ url: string }> {
    await this.delay(500)

    // In a real app, this would return a pre-signed URL or trigger a download
    return {
      url: `/api/invoices/${invoiceId}/download`,
    }
  }

  async getUpgradeOptions(): Promise<PlanUpgradeOptions> {
    await this.delay()

    return {
      available_plans: [
        {
          id: 'plan-starter',
          name: 'Starter',
          price: 29,
          currency: 'USD',
          billing_period: 'monthly',
          features: [
            '10,000 events/month',
            'Up to 3 team members',
            'Basic analytics',
            'Email support',
          ],
          limits: {
            events_per_month: 10000,
            seats: 3,
            webhooks: 5,
            api_calls_per_day: 1000,
          },
        },
        {
          id: 'plan-pro',
          name: 'Professional',
          price: 99,
          currency: 'USD',
          billing_period: 'monthly',
          features: [
            'Unlimited events',
            'Up to 10 team members',
            'Advanced analytics',
            'Priority support',
            'Custom webhooks',
            'API access',
          ],
          limits: {
            events_per_month: null,
            seats: 10,
            webhooks: 50,
            api_calls_per_day: 10000,
          },
        },
        {
          id: 'plan-enterprise',
          name: 'Enterprise',
          price: 299,
          currency: 'USD',
          billing_period: 'monthly',
          features: [
            'Unlimited everything',
            'Unlimited team members',
            'Custom integrations',
            'Dedicated support',
            'SLA guarantee',
            'Custom contracts',
          ],
          limits: {
            events_per_month: null,
            seats: null,
            webhooks: null,
            api_calls_per_day: null,
          },
          upgrade_cost: 200, // prorated for current period
        },
      ],
    }
  }

  async changePlan(
    planId: string,
    billing_period: 'monthly' | 'yearly' = 'monthly'
  ): Promise<CurrentPlan> {
    await this.delay(1200)

    const upgradeOptions = await this.getUpgradeOptions()
    const newPlan = upgradeOptions.available_plans.find(p => p.id === planId)

    if (!newPlan) {
      throw new Error('Plan not found')
    }

    // Update current plan
    this.billingData.current_plan = {
      ...this.billingData.current_plan,
      id: newPlan.id,
      name: newPlan.name,
      price: newPlan.price,
      billing_period: billing_period,
      features: newPlan.features,
      limits: newPlan.limits,
    }

    return { ...this.billingData.current_plan }
  }

  async cancelSubscription(): Promise<void> {
    await this.delay(800)

    this.billingData.current_plan.status = 'cancelled'
    this.billingData.current_plan.auto_renew = false
  }

  async reactivateSubscription(): Promise<void> {
    await this.delay(800)

    this.billingData.current_plan.status = 'active'
    this.billingData.current_plan.auto_renew = true
  }
}

export const billingService = new BillingService()
